// react
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// components
import { Avatar } from '@/components/atoms/Avatar/Avatar';
import { Button } from '@/components/atoms/Button/Button';

/**
 * ProfilePreview is a preview of a user's profile.
 */
export const ProfilePreview = ({
  imageSrc,
  fallback,
  name,
  role,
  careCircle,
  relationship,
  email,
  kindeEmail,
  landlineNumber,
  mobileNumber,
  address,
  postcode,
  onClose,
  toViewFullProfile,
  onViewFullProfile,
  actions,
  isActionsDisabled = false,
}: ProfilePreviewProps) => {
  const labelItem = useCallback(
    (label: string, value: string) => (
      <LabelItem>
        <Label>{label}</Label>
        <Value>{value}</Value>
      </LabelItem>
    ),
    [],
  );
  return (
    <StyledProfilePreview>
      <Body>
        <Avatar src={imageSrc} fallback={fallback} size='lg' />
        <InformationContainer>
          <Main>
            <Name>{name}</Name>
            {role && <Role>{role}</Role>}
          </Main>
          <Section>
            {careCircle && labelItem('Care circle', careCircle)}
            {relationship && labelItem('Relationship', relationship)}
            {email && labelItem('Email', email)}
            {kindeEmail && labelItem('Kinde Email', kindeEmail)}
            {!kindeEmail && labelItem('Kinde Email', 'Not migrated to Kinde')}
            {landlineNumber && labelItem('Landline number', landlineNumber)}
            {mobileNumber && labelItem('Mobile number', mobileNumber)}
            {address && labelItem('Address', address)}
            {postcode && labelItem('Postcode', postcode)}
          </Section>
        </InformationContainer>
      </Body>
      {actions && actions.length > 0 && (
        <ActionGroup>
          {actions?.map((action) => (
            <Button
              ariaLabel={action.label}
              variant='primary'
              onPress={action.onPress}
              isDisabled={action.isDisabled}
            >
              {action.label}
            </Button>
          ))}
        </ActionGroup>
      )}
      {!isActionsDisabled && (
        <ActionGroup>
          <Button ariaLabel='close' variant='secondary' onPress={onClose}>
            Close
          </Button>
          <Button
            ariaLabel='view full profile'
            variant='primary'
            iconRight={'long-arrow-right'}
            to={toViewFullProfile}
            onPress={onViewFullProfile}
          >
            View full profile
          </Button>
        </ActionGroup>
      )}
    </StyledProfilePreview>
  );
};

const StyledProfilePreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[20]};

  ${(props) =>
    props.theme.breakpoint.sm(css`
      gap: ${props.theme.spacing[48]};
    `)}
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[20]};

  ${(props) =>
    props.theme.breakpoint.sm(css`
      flex-direction: row;
      gap: ${props.theme.spacing[36]};
    `)}
`;

const InformationContainer = styled.div`
  display: flex;
  flex-direction: column;
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.neutral[90]};
  gap: ${(props) => props.theme.spacing[24]};
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${(props) => props.theme.spacing[20]};
  gap: ${(props) => props.theme.spacing[4]};
`;

const Name = styled.div`
  font: ${(props) => props.theme.typography.heading['5xl']};
  color: ${(props) => props.theme.color.primary[80]};
  white-space: nowrap;
`;

const Role = styled.div`
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.neutral[100]};
  white-space: nowrap;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[20]};
`;

const LabelItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[4]};
  border-spacing: 0;
`;

const Label = styled.div`
  font: ${(props) => props.theme.typography.body.md.bold};
  padding-right: ${(props) => props.theme.spacing[32]};
  color: ${(props) => props.theme.color.neutral[90]};
  white-space: nowrap;
`;

const Value = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
  white-space: nowrap;
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
`;

interface Action {
  label: string;
  onPress: () => void;
  isDisabled?: boolean;
}

interface ProfilePreviewProps {
  imageSrc?: string;
  fallback: string;
  name: string;
  role?: string;
  careCircle?: string;
  relationship?: string;
  email?: string;
  kindeEmail?: string;
  landlineNumber?: string;
  mobileNumber?: string;
  address?: string;
  postcode?: string;
  onClose?: () => void;
  toViewFullProfile?: string;
  onViewFullProfile?: () => void;
  actions?: Action[];
  isActionsDisabled?: boolean;
}

ProfilePreview.displayName = 'ProfilePreview';

export default ProfilePreview;
